import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Helper: returns headers with Authorization token if available
const authHeaders = (extra = {}) => {
  const token = localStorage.getItem('token');
  return {
    ...extra,
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
};

// Wraps fetch with a friendly error when the backend can't be reached.
// A bare `TypeError: Failed to fetch` is what users see today; translating that
// into actionable text ("backend not reachable") avoids confusion when the
// Render service is cold-starting or REACT_APP_BACKEND_URL isn't set.
async function safeFetch(url, options) {
  try {
    return await fetch(url, options);
  } catch (err) {
    const hint = API_URL.includes('localhost')
      ? 'The backend server is not running on localhost:8000.'
      : `Can't reach the server at ${API_URL}. It may be waking up — try again in 30s, or check that the backend is deployed.`;
    const wrapped = new Error(hint);
    wrapped.cause = err;
    wrapped.isNetworkError = true;
    throw wrapped;
  }
}

// Demo-mode user — used as a fallback when the backend is unreachable so the
// product is still walkable for demos, portfolio views, and local dev without
// Docker. Gated to the documented credentials to avoid pretending random
// emails are authenticated.
const DEMO_CREDENTIALS = {
  'owner@rentroom.com': 'owner123',
  'demo@roomhora.com': 'demo',
};
const DEMO_USER = {
  user_id: 'demo-user',
  name: 'Demo Owner',
  email: 'owner@rentroom.com',
  role: 'owner',
  picture: null,
  is_verified_owner: true,
  preferred_currency: 'USD',
};
const DEMO_TOKEN = 'demo-session-token';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Rehydrate a demo session across refreshes when the token matches.
    const storedToken = localStorage.getItem('token');
    const storedDemoUser = localStorage.getItem('demo_user');
    if (storedToken === DEMO_TOKEN && storedDemoUser) {
      try {
        setUser(JSON.parse(storedDemoUser));
      } catch {
        localStorage.removeItem('demo_user');
      }
      setLoading(false);
      return;
    }

    try {
      const response = await safeFetch(`${API_URL}/api/auth/me`, {
        headers: authHeaders(),
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      // Silent failure on initial check — we just treat the user as logged out
      // and let the login page surface backend reachability issues.
      if (!error.isNetworkError) {
        console.error('Auth check failed:', error);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const response = await safeFetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }

      const data = await response.json();
      setUser(data.user);
      localStorage.setItem('token', data.access_token);
      return data;
    } catch (err) {
      // Backend unreachable → allow the documented demo credentials to log in
      // locally so the product is still walkable. Anything else re-throws.
      if (err.isNetworkError && DEMO_CREDENTIALS[email] === password) {
        const demoUser = { ...DEMO_USER, email };
        setUser(demoUser);
        localStorage.setItem('token', DEMO_TOKEN);
        localStorage.setItem('demo_user', JSON.stringify(demoUser));
        return { user: demoUser, access_token: DEMO_TOKEN, demo: true };
      }
      throw err;
    }
  };

  const register = async (name, email, password) => {
    try {
      const response = await safeFetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Registration failed');
      }

      const data = await response.json();
      setUser(data.user);
      localStorage.setItem('token', data.access_token);
      return data;
    } catch (err) {
      // Network error during registration — create a local demo session so the
      // user can still explore the app while the backend is being deployed.
      if (err.isNetworkError) {
        const demoUser = { ...DEMO_USER, name: name || 'Demo User', email };
        setUser(demoUser);
        localStorage.setItem('token', DEMO_TOKEN);
        localStorage.setItem('demo_user', JSON.stringify(demoUser));
        return { user: demoUser, access_token: DEMO_TOKEN, demo: true };
      }
      throw err;
    }
  };

  const logout = async () => {
    try {
      await safeFetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: authHeaders(),
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('demo_user');
  };

  const updateRole = async (role) => {
    const response = await safeFetch(`${API_URL}/api/auth/role?role=${role}`, {
      method: 'PUT',
      headers: authHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to update role');
    }

    setUser(prev => ({ ...prev, role }));
  };

  const loginWithGoogle = async (credential) => {
    const response = await safeFetch(`${API_URL}/api/auth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Google login failed');
    }

    const data = await response.json();
    setUser(data.user);
    localStorage.setItem('token', data.access_token);
    return data;
  };

  const loginWithFacebook = async (accessToken) => {
    const response = await safeFetch(`${API_URL}/api/auth/facebook`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_token: accessToken }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Facebook login failed');
    }

    const data = await response.json();
    setUser(data.user);
    localStorage.setItem('token', data.access_token);
    return data;
  };

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      login,
      register,
      logout,
      updateRole,
      loginWithGoogle,
      loginWithFacebook,
      checkAuth,
      isAuthenticated: !!user,
      isOwner: user?.role === 'owner' || user?.role === 'admin',
      isAdmin: user?.role === 'admin',
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
